import {
  IframeLayout,
  SeparatorImg,
  SeparatorMoon,
  Box,
  Title,
  Image,
  GlobalPayTokenResponse,
  IQ11CreditCard,
  FlexiblePaymentMethods,
  ConnectionTypes,
  chooseColor,
} from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../providers/Layout.provider';
import { useIQ11UserLogin } from '../../../providers/IQ11Login.provider';
import { useAccessTokenFields } from '../../../providers/AccessToken.provider';
import { useToken } from '../../../providers/Token.provider';
import { usePaylink } from '../../../providers/Paylink.provider';
import useStorage from '../../../hooks/useStorage';
import CreditCardForm from './CreditCardForm';
import useFetchCreditCards from '../../../hooks/useApi/useFetchCreditCards';
import useFetchGlobalPayToken from '../../../hooks/useApi/useFetchGlobalPayToken';
import usePostLinkCreditCard from '../../../hooks/useApi/usePostLinkCreditCard';
import usePostPaylinkSetAccount from '../../../hooks/useApi/usePostPaylinkSetAccount';
import { useFlexiblePayment } from '../../../providers/FlexiblePayment.provider';
import AgreePADContext, { AgreePADCtx } from './Dialogue/AgreePADContext';
import AgreePADDialogue from './Dialogue/AgreePADDialogue';
import ConnectCreditCardContext, { ConnectCreditCardCtx } from './ConnectCreditCardContext';
import ConnectCreditCardDialogue from './Dialogue/ConnectCreditCardDialogue';
import { ConnectCreditCardDialogueHandler, DialogueHandlerProps } from './types';
import { AgreePADDialogueHandler, DialogueHandlerPadProps } from './Dialogue/types';
import useDataFetchIndicator from '../../../hooks/useDataFetchIndicator';
import BrandingLogo from '../../UI/BrandingLogo/BrandingLogo';

type FC = FunctionComponent;

const CreditCardContainer: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'CreditCardForm' });
  const history = useHistory();
  const { Token } = usePaylink();
  const { token, iQ11ForOnboarding, isSetByBrowser } = useToken();
  const { accessToken, paymentMethod } = useAccessTokenFields();
  const { layout, color, src, height, positionX, positionY, hasBackButton, setLayoutInfo } = useLayout();
  const storage = useStorage();
  const { padInfo } = storage.padAgreement.getPadInfo();

  const [CardNumber, setCardNumber] = useState('');
  const [ExpirationDate, setExpirationDate] = useState('');
  const [CVV, setCVV] = useState('');

  const { response } = useIQ11UserLogin();

  const [, , fetchGlobalPayToken, resetGlobalPay] = useFetchGlobalPayToken({ bearer: accessToken });
  const [globalPayResponse, setGlobalPayResponse] = useState<GlobalPayTokenResponse>();
  const [linkCreditCardResponse, postLinkCreditCard] = usePostLinkCreditCard({});
  const [creditCardResponse, fetchCreditCards] = useFetchCreditCards({ bearer: response.jwt });
  const [paylinkAccountResponse, setPaylinkAccount] = usePostPaylinkSetAccount({});
  const { paymentMethods, paymentType, addPaymentMethod, isFlexiblePayment, partialPaymentValues, installmentDetails } =
    useFlexiblePayment();
  const ClientAccountID = response?.clientAccount?.ClientAccountID || '';
  const [hasAgreed, setHasAgreed] = useState(false);
  const [shouldConnectCreditCard, setShouldConnectCreditCard] = useState(false);
  const [connectCreditCardError, setConnectCreditCardError] = useState('');
  const [showConnectCreditCardErrorAlert, setShowConnectCreditCardErrorAlert] = useState(false);
  const [showConnectCreditCardSuccessAlert, setShowConnectCreditCardSuccessAlert] = useState(false);
  const [showConnectSameCreditCardErrorAlert, setShowConnectSameCreditCardErrorAlert] = useState(false);
  const [showConnectCreditCardFailAlert, setShowConnectCreditCardFailAlert] = useState(false);
  const [showAgreePADSuccessAlert, setShowAgreePADSuccessAlert] = useState(false);
  const [buttonDisableState, setButtonDisableState] = useState(false);
  const [isLinkCreditCard, setIsLinkCreditCard] = useState(false);
  const [creditCardPropsSaved, setCreditCardPropsSaved] = useState<IQ11CreditCard>();
  const [FullName, setFullName] = useState('');
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const ConnectCreditCard: ConnectCreditCardCtx = {
    showConnectCreditCardSuccessAlert,
    showConnectCreditCardErrorAlert,
    showConnectSameCreditCardErrorAlert,
    showConnectCreditCardFailAlert,
    connectCreditCardError,
  };

  const showBackButton = !(isSetByBrowser && paymentMethod === 'credit');
  const defaultValues: IQ11CreditCard = {
    FullName: '',
    CardNumber: '',
    ExpirationDate: '',
    CVV: '',
    Address: '',
    Brand: '',
  };

  const handlers = {
    handleConnectCreditCardDialogue: (handlerProps: DialogueHandlerProps<ConnectCreditCardDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: ConnectCreditCardDialogueHandler = {
        create: {
          success: {
            default: () => {
              const redirectMethod = token?.RedirectMethod;
              const url = `${token?.RedirectURL || ''}?Token=${linkCreditCardResponse.Token}&PaymentMethod=creditcard`;
              setShowConnectCreditCardSuccessAlert(false);
              if (redirectMethod === 'InnerRedirect') {
                window.location.href = url;
              } else if (redirectMethod === 'OuterRedirect') {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                window.top!.location.href = url;
              }
            },
          },
          fail: {
            default: () => {
              setShowConnectCreditCardErrorAlert(false);
              setShowConnectSameCreditCardErrorAlert(false);
              setConnectCreditCardError('');
            },
          },
        },
        connect: {
          success: {
            default: () => {
              setShowConnectCreditCardFailAlert(false);
              window.location.reload();
            },
          },
          fail: {
            default: () => {},
          },
        },
        agree: {
          success: {
            default: () => {
              setShowAgreePADSuccessAlert(false);
            },
          },
          fail: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },

    onConnectCreditCard: (values: IQ11CreditCard) => {
      if (padInfo === 'agreed') {
        setButtonDisableState(true);
        setCardNumber(values.CardNumber);
        setExpirationDate(values.ExpirationDate);
        setCVV(values.CVV);
        setFullName(values.FullName);
      } else {
        setCreditCardPropsSaved(values);
        setShowAgreePADSuccessAlert(true);
        setButtonDisableState(false);
      }
    },
  };
  const AgreePAD: AgreePADCtx = {
    showAgreePADSuccessAlert,
  };
  const handlersPad = {
    handleAgreePADDialogue: (handlerProps: DialogueHandlerPadProps<AgreePADDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: AgreePADDialogueHandler = {
        agree: {
          success: {
            default: () => {
              setHasAgreed(true);
              storage.padAgreement.storePadInfo('agreed');
              setShowAgreePADSuccessAlert(false);
            },
          },
          pdf: {
            default: () => {
              setShowAgreePADSuccessAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowAgreePADSuccessAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };
  useEffect(() => {
    if (padInfo === 'agreed' && hasAgreed && creditCardPropsSaved) {
      handlers.onConnectCreditCard(creditCardPropsSaved);
    }
  }, [padInfo]);
  useEffect(() => {
    storage.paymentMethod.storePaymentMethod('credit');
  }, []);

  useEffect(() => {
    if (Token) {
      setLayoutInfo(
        chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
        'moon',
        '',
        80,
        20,
        0,
        true,
        false,
      );
    } else {
      setLayoutInfo(
        chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
        'moon',
        '',
        0,
        0,
        0,
        showBackButton,
        false,
      );
    }
  }, []);

  useEffect(() => {
    fetchCreditCards({
      ClientAccountID,
    });
  }, [response]);

  // 'ucp/payment-methods'
  // 'credit-card/link-credit-card'
  useEffect(() => {
    if (CVV.length > 1) {
      const handleGlobalPay = async () => {
        setDataFetchingInfo(true, `${t('Loading')}`, 'fullScreen');
        try {
          const globalPayTokenResponse = await fetchGlobalPayToken({
            reference: generateGuid(),
            card: {
              cvv: CVV,
              expiry_month: ExpirationMonth(),
              expiry_year: ExpirationYear(),
              number: CardNumber.replace(/\s/g, ''),
            },
            usage_mode: 'SINGLE',
          });
          setGlobalPayResponse(globalPayTokenResponse);
          setDataFetchingInfo(false);

          if (globalPayTokenResponse.id.length > 1) {
            const cardNumValidation = Object.values(creditCardResponse.CreditCards || {})
              .map((card) => card.CreditCardNumber)
              .includes(globalPayTokenResponse.card.masked_number_last4);

            let validation = true;
            if (cardNumValidation) {
              validation = false;
              setButtonDisableState(false);
              resetGlobalPay();
              setShowConnectSameCreditCardErrorAlert(true);
            }

            if (validation) {
              postLinkCreditCard({
                PaymentToken: globalPayTokenResponse.id,
                CardHolderName: FullName,
                MaskedCardNumber: globalPayTokenResponse.card.masked_number_last4,
                CardBrand: globalPayTokenResponse.card.brand,
                ExpiryYear: globalPayTokenResponse.card.expiry_year,
                ExpiryMonth: globalPayTokenResponse.card.expiry_month,
                ForOnboarding: iQ11ForOnboarding || false,
              });
              setShouldConnectCreditCard(true);
            }
          } else {
            setDataFetchingInfo(false);
            setButtonDisableState(false);
            setShowConnectCreditCardErrorAlert(true);
            resetGlobalPay();
          }
        } catch (error) {
          setDataFetchingInfo(false);
          setButtonDisableState(false);
          setShowConnectCreditCardErrorAlert(true);
          resetGlobalPay();
        }
      };

      // eslint-disable-next-line no-void
      void handleGlobalPay();
    }
  }, [CVV, CardNumber]);

  const completePaylink = () => {
    if (isFlexiblePayment) {
      if (paymentType === FlexiblePaymentMethods.MultiplePaymentMethods) {
        const paymentMethodFind = paymentMethods.find((method) => method.method === ConnectionTypes.Credit);
        addPaymentMethod({
          ...paymentMethodFind,
          amount: paymentMethodFind?.amount || 0,
          token: linkCreditCardResponse.Token,
          account: globalPayResponse?.card.masked_number_last4 || '',
          method: ConnectionTypes.Credit,
        });
        history.push('/connect/flexible-payment/multiple-payment-methods');
      }
      if (paymentType === FlexiblePaymentMethods.PartialPayment) {
        storage.padAgreement.clearPadInfo();
        setPaylinkAccount({
          SingleUseToken: Token,
          Iq11Token: linkCreditCardResponse.Token,
          PaymentMethod: 'credit',
          FlexiblePaymentOptions: {
            PaymentOption: 'partial',
            PartialPaymentDetails: partialPaymentValues,
          },
        });
      }
      if (paymentType === FlexiblePaymentMethods.InstallmentPlan) {
        storage.padAgreement.clearPadInfo();
        setPaylinkAccount({
          SingleUseToken: Token,
          Iq11Token: linkCreditCardResponse.Token,
          PaymentMethod: 'credit',
          FlexiblePaymentOptions: {
            PaymentOption: 'installments',
            InstallmentDetails: installmentDetails,
          },
        });
      }
    } else {
      storage.padAgreement.clearPadInfo();
      setPaylinkAccount({
        SingleUseToken: Token,
        Iq11Token: linkCreditCardResponse.Token,
        PaymentMethod: 'credit',
      });
    }
    setIsLinkCreditCard(true);
  };

  // 'paylink/set-account'
  useEffect(() => {
    if (!shouldConnectCreditCard) return;
    setShouldConnectCreditCard(false);

    const { Success, ErrorMessage } = linkCreditCardResponse;

    if (!Success) {
      setConnectCreditCardError(ErrorMessage);
      setButtonDisableState(false);
      setShowConnectCreditCardErrorAlert(true);
      return;
    }

    if (Token) {
      completePaylink();
    } else {
      storage.padAgreement.clearPadInfo();

      if (isSetByBrowser && globalPayResponse) {
        const redirectMethod = token?.RedirectMethod;
        const url = `${token?.RedirectURL || ''}?Token=${linkCreditCardResponse.Token}&PaymentMethod=creditcard`;

        const Response = {
          Step: 'LINK',
          Url: url,
          CreditCardNumber: globalPayResponse.card.masked_number_last4,
          CreditCardHolderName: FullName,
          CreditCardBrand: globalPayResponse.card.brand,
          CreditCardExpiryYear: globalPayResponse.card.expiry_year,
          CreditCardExpiryMonth: globalPayResponse.card.expiry_month,
          Token: linkCreditCardResponse.Token,
          PaymentMethod: 'creditcard',
        };

        // Inner and outer redirect
        window.top?.postMessage(Response, '*');
        if (window.parent && window.parent !== window.top) {
          window.parent.postMessage(Response, '*');
        }

        setTimeout(() => {
          if (redirectMethod === 'InnerRedirect') {
            window.location.href = url;
          } else if (redirectMethod === 'OuterRedirect') {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            window.top!.location.href = url;
          } else {
            history.push('/success');
          }
        }, 300);
      } else {
        history.push('/dashboard');
      }
    }
  }, [linkCreditCardResponse, globalPayResponse]);

  useEffect(() => {
    if (isLinkCreditCard && paylinkAccountResponse) {
      setIsLinkCreditCard(false);
      if (paylinkAccountResponse.Success) {
        history.push('/thank-you', {
          paymentMethodType: 'credit',
          paymentMethod: globalPayResponse?.card.masked_number_last4 || '',
        });
      } else {
        setButtonDisableState(false);
        setShowConnectCreditCardFailAlert(true);
      }
    }
  }, [paylinkAccountResponse]);

  return (
    <IframeLayout
      primaryColor={color}
      separator={
        layout === 'img' ? (
          <SeparatorImg width={100} height={110} fill={color} />
        ) : layout === 'moon' ? (
          <SeparatorMoon width={140} height={110} fill={color} />
        ) : (
          <></>
        )
      }
      image={
        src === '' ? (
          <></>
        ) : (
          <Image width="auto" height={height} positionX={positionX} positionY={positionY} src={src} />
        )
      }
      hasBackButton={hasBackButton}
      content={
        <>
          {token?.BrandingDetails?.Logo && (
            <Box pb="lg" jc="center">
              <BrandingLogo src={token?.BrandingDetails?.Logo} />
            </Box>
          )}
          <Box mb="md" flx="col" height="auto">
            <Title title={`${t('title')}`} size="lg" bold />
          </Box>

          <ConnectCreditCardContext.Provider value={ConnectCreditCard}>
            <Box jc="center">
              <Box maxwidth="465px">
                <CreditCardForm
                  defaultValues={defaultValues}
                  handlers={handlers}
                  buttonDisableState={buttonDisableState}
                />
                <ConnectCreditCardDialogue handleDialogue={handlers.handleConnectCreditCardDialogue} />
              </Box>
            </Box>
          </ConnectCreditCardContext.Provider>
          <AgreePADContext.Provider value={AgreePAD}>
            <AgreePADDialogue handleDialogue={handlersPad.handleAgreePADDialogue} />
          </AgreePADContext.Provider>
        </>
      }
    />
  );

  function generateGuid() {
    const S4 = function () {
      // eslint-disable-next-line no-bitwise
      return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    };
    return `${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
  }

  function ExpirationMonth() {
    return ExpirationDate.split('/')[0];
  }

  function ExpirationYear() {
    return ExpirationDate.split('/')[1];
  }
};

export default CreditCardContainer;
